import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";

import {
	Button,
	FormControl,
	Select,
	MenuItem,
	Snackbar,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	DialogContentText,
	TextField,
	CircularProgress,
	Typography
} from "@mui/material";
import { Close, Add, DeleteOutline } from "@mui/icons-material";
import { THEME_MAP } from "../theme";

import { useManagers, useUserUtils } from "../actions/users";
import { useUserRoles } from "../globalstore";
import { useViewedBar, useBarUtils } from "../actions/bars";
import { useIsMobile } from "../hooks/useIsMobile";

import firebase from "firebase";
import { captureError } from "../lib/errorReporting";

export function BarManagers() {
	const { id: barId } = useParams();
	const managers = useManagers();
	const { fetchManagers, addManager } = useUserUtils();
	const selectedBar = useViewedBar();
	const { fetchBarById } = useBarUtils();
	const isMobile = useIsMobile();

	const [isAddingManager, setIsAddingManager] = useState(false);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [email, setEmail] = useState("");

	const [isNoteOpen, setIsNoteOpen] = useState(false);
	const [note, setNote] = useState("");

	// Track pending role changes: { [userId]: newRole }
	const [pendingChanges, setPendingChanges] = useState({});
	const hasPendingChanges = Object.keys(pendingChanges).length > 0;

	const closeNote = (_event, reason) => {
		if (reason === "clickaway") {
			return;
		}
		setNote("");
		setIsNoteOpen(false);
	};

	const openNote = (message) => {
		setNote(message);
		setIsNoteOpen(true);
	};

	useEffect(() => {
		barId && fetchBarById(barId);
	}, [barId, fetchBarById]);

	useEffect(() => {
		selectedBar?.id && fetchManagers(selectedBar);
	}, [selectedBar, fetchManagers]);

	const toggleDialog = () => {
		setEmail("");
		setIsDialogOpen((c) => !c);
	};

	const sendResetEmail = async () => {
		await firebase.auth().sendPasswordResetEmail(email);
	};

	const resolveBarName = (bar) => {
		if (!bar) {
			return "";
		}

		if (typeof bar.get === "function") {
			return (
				bar.get("bar_name") ||
				bar.get("barName") ||
				bar.get("name") ||
				""
			);
		}

		return bar.barName || bar.bar_name || bar.name || "";
	};

	const addNewManager = () => {
		if (!selectedBar?.id) {
			openNote("Select a bar before adding a manager.");
			return;
		}

		const barName = resolveBarName(selectedBar);
		if (!barName) {
			openNote("Bar name is missing. Try refreshing the bar details and retry.");
			return;
		}

		setIsAddingManager(true);
		addManager({
			email: email.toLowerCase(),
			barId: selectedBar.id,
			barName,
			role: "manager"
		})
			.then(() => {
				sendResetEmail();
				setIsAddingManager(false);
				setIsDialogOpen(false);
				openNote("Successfully Added user");
			})
			.catch((e) => {
				setIsAddingManager(false);
				openNote(e.message);
				captureError(e, { action: "addManager" });
			});
	};

	const handlePendingRoleChange = (userId, change) => {
		setPendingChanges((prev) => ({ ...prev, [userId]: change }));
	};

	const barName = resolveBarName(selectedBar);

	return (
		<Container>
			<div className="content-area">
			<div className="filters" data-tour="managers-add">
				<IconButton
					color="primary"
					onClick={toggleDialog}
					aria-label="Add manager"
					sx={{
						backgroundColor: "#1976d2",
						color: "#fff",
						"&:hover": {
							backgroundColor: "#1565c0"
						},
						width: 40,
						height: 40
					}}
				>
					<Add />
				</IconButton>
			</div>

			{barName && (
				<BarNameHeader>
					{barName}
				</BarNameHeader>
			)}

			<div className="users-container" data-tour="managers-list">
				{managers?.map((snapshot) => (
					<UserListItem
						key={snapshot.id}
						userSnapshot={snapshot}
						openNote={openNote}
						isMobile={isMobile}
						onPendingChange={handlePendingRoleChange}
						pendingRole={pendingChanges[snapshot.id]?.role}
					/>
				))}
			</div>

			{hasPendingChanges && (
				<SaveAllButton
					managers={managers}
					pendingChanges={pendingChanges}
					setPendingChanges={setPendingChanges}
					openNote={openNote}
				/>
			)}
			</div>

			<Snackbar
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right"
				}}
				open={isNoteOpen}
				autoHideDuration={3000}
				onClose={closeNote}
				message={note}
				action={
					<IconButton
						size="small"
						aria-label="close"
						color="inherit"
						onClick={closeNote}
					>
						<Close fontSize="small" />
					</IconButton>
				}
			/>
			<Dialog open={isDialogOpen} onClose={toggleDialog}>
				<DialogTitle>Add manager</DialogTitle>
				<DialogContent>
					<DialogContentText>
						To add a manager please enter their email address
						and submit.
					</DialogContentText>
					<TextField
						autoFocus
						margin="dense"
						label="Email Address"
						type="email"
						fullWidth
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						disabled={isAddingManager}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={toggleDialog}
						color="primary"
						disabled={isAddingManager}
					>
						Cancel
					</Button>
					<div
						style={{
							display: "inline-block",
							position: "relative"
						}}
					>
						<Button
							onClick={addNewManager}
							variant="contained"
							color="primary"
							disabled={isAddingManager}
						>
							Add manager
						</Button>
						{isAddingManager && (
							<CircularProgress
								style={{
									position: "absolute",
									width: "24px",
									height: "24px",
									top: "calc(50% - 12px)",
									left: "calc(50% - 12px)"
								}}
							/>
						)}
					</div>
				</DialogActions>
			</Dialog>
		</Container>
	);
}

/**
 * SaveAllButton — appears when any role has been changed.
 * Commits all pending changes at once.
 */
function SaveAllButton({ managers, pendingChanges, setPendingChanges, openNote }) {
	const { setUserRole } = useUserUtils();
	const barSnapshot = useViewedBar();
	const [saving, setSaving] = useState(false);

	const handleSaveAll = async () => {
		setSaving(true);
		const entries = Object.entries(pendingChanges);
		let failures = 0;

		for (const [userId, { role }] of entries) {
			const snapshot = managers.find((m) => m.id === userId);
			if (!snapshot) continue;
			const user = { id: snapshot.id, ...(snapshot.data?.() || {}) };
			try {
				await setUserRole(user, barSnapshot, role);
			} catch (err) {
				captureError(err, { action: "batchSaveUserRole", userId });
				failures++;
			}
		}

		setSaving(false);
		setPendingChanges({});

		if (failures === 0) {
			openNote("All changes saved");
		} else {
			openNote(`Saved with ${failures} error(s)`);
		}
	};

	return (
		<SaveBar>
			<Button
				variant="contained"
				color="primary"
				onClick={handleSaveAll}
				disabled={saving}
				fullWidth
			>
				{saving ? "Saving…" : "Save Changes"}
			</Button>
		</SaveBar>
	);
}

const SaveBar = styled.div`
	padding: 12px 0 4px;
`;

const BarNameHeader = styled.div`
	font-size: 1rem;
	font-weight: 500;
	color: ${THEME_MAP["color-basic-700"]};
	padding: 8px 16px;
	background: ${THEME_MAP["color-basic-100"]};
	border: 1px solid ${THEME_MAP["color-basic-400"]};
	border-bottom: none;
	border-radius: 4px 4px 0 0;
`;

const Container = styled.div`
	height: 100%;
	width: 100%;

	overflow: auto;

	padding: 24px;

	.input {
		background: #fff;
	}

	.content-area {
		width: 100%;
	}

	.filters {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		margin-bottom: 16px;
	}

	.users-container {
		width: 100%;
		min-height: 50px;
		border-radius: 0 0 4px 4px;
		border: 1px solid ${THEME_MAP["color-basic-400"]};
		background: ${THEME_MAP["color-basic-100"]};
		margin-bottom: 16px;
	}

	@media (max-width: 767px) {
		padding: 16px;

		.users-container {
			border-radius: 0 0 4px 4px;
		}
	}
`;

const ROLES = ["owner", "manager"];

function UserListItem({ userSnapshot, openNote, isMobile, onPendingChange, pendingRole }) {
	const { userRoles: { roles: currentUserRoles, isAdmin }} = useUserRoles();
	const { id: barId } = useParams();

	const barSnapshot = useViewedBar();

	const [currentRef] = useState(userSnapshot.ref);
	const [user, setUser] = useState({
		id: userSnapshot?.id,
		...(userSnapshot?.data() || {})
	});

	const [showDeleteAlert, setShowDeleteAlert] = useState(false);
	const [editingRole, setEditingRole] = useState(false);

	const { setUserRole, removeBarFromUser } = useUserUtils();

	useEffect(() => {
		if (currentRef) {
			return currentRef.onSnapshot((snap) => {
				setUser({ id: snap.id, ...snap.data() });
			}, (err) => {
				captureError(err, { view: "BarManagers", action: "user.onSnapshot" });
				console.error("BarManagers user snapshot error", err);
			});
		}
	}, [currentRef]);

	const currentRole = user.roles?.[barId]?.role || "";
	const displayRole = pendingRole || currentRole;

	const handleRoleChange = (e) => {
		const newRole = e.target.value;
		setEditingRole(false);

		if (newRole === currentRole) return;

		if (isMobile) {
			// Defer to parent for batch save
			onPendingChange(user.id, { role: newRole });
		} else {
			// Desktop: save immediately (existing behavior)
			setUserRole(user, barSnapshot, newRole)
				.then(() => {
					openNote("Successfully updated user");
				})
				.catch((err) => {
					openNote("We were unable to update the user");
					captureError(err, { action: "setUserRole" });
				});
		}
	};

	const removeBarPermissions = () => {
		removeBarFromUser(user, barId);
	};

	const canEditRoles =
		isAdmin || currentUserRoles?.[barId]?.role === "owner";

	const dialogView = (
		<Dialog
			open={showDeleteAlert}
			onClose={() => setShowDeleteAlert(false)}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
		>
			<DialogTitle id="alert-dialog-title">
				{"Are you sure?"}
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="alert-dialog-description">
					Would you like to proceed with deleting this manager?
					<br />
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={() => setShowDeleteAlert(false)}
					color="primary"
				>
					Cancel
				</Button>
				<Button
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						removeBarPermissions();
					}}
					color="primary"
					autoFocus
				>
					Delete
				</Button>
			</DialogActions>
		</Dialog>
	);

	return (
		<PermissionRow $hasPending={!!pendingRole}>
			<div className="manager-row">
				<Typography variant="body2" className="manager-email" noWrap>
					{user?.email || "Email"}
				</Typography>
				<div className="manager-actions">
					{editingRole ? (
						<FormControl size="small" className="role-select">
							<Select
								value={displayRole}
								onChange={handleRoleChange}
								onClose={() => setEditingRole(false)}
								open={editingRole}
								size="small"
								disabled={!canEditRoles}
							>
								{ROLES.map((role) => (
									<MenuItem key={role} value={role}>
										{role}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					) : (
						<RoleChip
							onClick={() => canEditRoles && setEditingRole(true)}
							$canEdit={canEditRoles}
							$hasPending={!!pendingRole}
						>
							{displayRole}
						</RoleChip>
					)}
					<IconButton
						size="small"
						onClick={() => setShowDeleteAlert(true)}
						disabled={!canEditRoles}
						sx={{ color: THEME_MAP["color-danger-500"], ml: 0.5 }}
					>
						<DeleteOutline fontSize="small" />
					</IconButton>
				</div>
			</div>
			{dialogView}
		</PermissionRow>
	);
}

const RoleChip = styled.span`
	display: inline-flex;
	align-items: center;
	padding: 4px 12px;
	border-radius: 16px;
	font-size: 0.8rem;
	font-weight: 500;
	text-transform: capitalize;
	cursor: ${({ $canEdit }) => ($canEdit ? "pointer" : "default")};
	background: ${({ $hasPending }) =>
		$hasPending
			? THEME_MAP["color-warning-transparent-200"]
			: THEME_MAP["color-basic-300"]};
	color: ${THEME_MAP["color-basic-800"]};
	border: 1px solid ${({ $hasPending }) =>
		$hasPending
			? THEME_MAP["color-warning-500"]
			: "transparent"};
	transition: background 0.15s ease;

	&:active {
		background: ${THEME_MAP["color-basic-400"]};
	}
`;

const PermissionRow = styled.div`
	padding: 12px 16px;
	border-bottom: 1px solid ${THEME_MAP["color-basic-300"]};

	&:last-child {
		border-bottom: none;
	}

	&:hover {
		background: ${THEME_MAP["color-primary-transparent-100"]};
	}

	.manager-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
	}

	.manager-email {
		flex: 1;
		min-width: 0;
		font-size: 0.875rem;
	}

	.manager-actions {
		display: flex;
		align-items: center;
		flex-shrink: 0;
	}

	.role-select {
		min-width: 100px;

		.MuiSelect-select {
			padding: 4px 8px;
			font-size: 0.8rem;
		}
	}
`;
